import { useMemo, useRef, useState } from 'react';
import type { LayerSpecification } from 'mapbox-gl';
import {
  UMAppShell,
  UMLayersPanel,
  UMMap,
  UMMapControls,
  UMMessage,
  UMR,
  UM_LAYER_CATALOG,
  UM_RAIL_ITEMS,
  type UMBasemapId,
  type UMMapHandle,
  type UMRailItemId,
} from '../_components';
import { MapToolbar, GEOMETRY_GROUP, TOOL_GROUPS, toolLabel } from './MapToolbar';
import { MeasureControls } from './MeasureControls';
import { GeometryEditBar } from './GeometryEditBar';
import { GEO_DISPLAYS, VARIANTS, useGeoDisplay, useVariant } from './hooks';
import type { ToolGroupSpec } from './types';

export const meta = {
  title: 'Barre de carte — ordre et classification des boutons',
  description:
    'Recherche, puis nom de la sélection, puis outils d’action. Trois classifications et quatre façons d’exposer les outils de géométrie.',
  tags: ['map', 'territory', 'designer'],
  author: 'Prototyper',
  updatedAt: '2026-10-21T16:57:54+02:00',
  status: 'draft' as const,
};

const LYON_BELLECOUR: [number, number] = [4.832, 45.7578];
/** Frames the demo buildings and pipes so the map is never empty on load. */
const BELLECOUR_BOUNDS: [[number, number], [number, number]] = [[4.8235, 45.7522], [4.8445, 45.7645]];

const DEMO_PIPES: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [
    { type: 'Feature', id: 1, properties: { id: 1 }, geometry: { type: 'LineString', coordinates: [[4.826, 45.7542], [4.8335, 45.7566], [4.8402, 45.7589]] } },
    { type: 'Feature', id: 2, properties: { id: 2 }, geometry: { type: 'LineString', coordinates: [[4.8295, 45.7612], [4.8358, 45.7598], [4.8421, 45.7604]] } },
  ],
};

const DEMO_BUILDINGS: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: Array.from({ length: 24 }, (_, i) => {
    const col = i % 6;
    const row = Math.floor(i / 6);
    const x = 4.8285 + col * 0.0012;
    const y = 45.7555 + row * 0.0011 + (row >= 2 ? 0.0012 : 0);
    const heat = [180, 420, 760, 1350, 95, 610][(i * 7) % 6] + i * 11;
    return {
      type: 'Feature' as const,
      id: i + 1,
      properties: { id: `BFR69000${String(i + 1).padStart(4, '0')}`, heat },
      geometry: { type: 'Polygon' as const, coordinates: [[[x, y], [x + 0.0009, y], [x + 0.0009, y + 0.0007], [x, y + 0.0007], [x, y]]] },
    };
  }),
};

const FOLDERS = ['Prospection IDF', 'Lyon Métropole'];

export default function BarreOutilsCartePrototype() {
  const mapRef = useRef<UMMapHandle>(null);
  const { variant, setVariant } = useVariant();
  const { geo, setGeo } = useGeoDisplay();
  const [panel, setPanel] = useState<UMRailItemId | null>(null);
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('Lyon Métropole');
  const [hasBuilding, setHasBuilding] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tool, setTool] = useState<string | null>(null);
  const [family, setFamily] = useState<ToolGroupSpec['id']>('buildings');
  const [basemap, setBasemap] = useState<UMBasemapId>('light');
  const [is3d, setIs3d] = useState(false);
  const [mode, setMode] = useState<'mapbox' | 'offline'>('offline');
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(UM_LAYER_CATALOG.map((g) => [g.id, !!g.defaultChecked])),
  );
  const [notice, setNotice] = useState<string | null>(null);

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 3000); };

  const layers = useMemo<LayerSpecification[]>(
    () => [
      {
        id: 'urbanmap-building-fill',
        type: 'fill',
        source: 'buildings',
        paint: {
          'fill-color': ['step', ['get', 'heat'], UMR.map.consumption[0], 300, UMR.map.consumption[1], 600, UMR.map.consumption[2], 1000, UMR.map.consumption[3]],
          'fill-opacity': 0.8,
        },
      },
      {
        id: 'urbanmap-pipe-line',
        type: 'line',
        source: 'pipes',
        layout: { visibility: checked['energy-networks'] ? 'visible' : 'none', 'line-cap': 'round' },
        paint: { 'line-color': UMR.map.pipe, 'line-width': UMR.map.pipeWidth },
      },
    ],
    [checked],
  );

  const railItems = useMemo(() => UM_RAIL_ITEMS.map((it) => (it.id === 'selection' ? { ...it, disabled: !folder } : it)), [folder]);
  const activeVariant = VARIANTS.find((v) => v.id === variant)!;
  const activeGeo = GEO_DISPLAYS.find((g) => g.id === geo)!;
  /** Controls aligned in the bar: search + selection + its button + locate, plus the tools. */
  const barItems = useMemo(() => {
    if (variant === 'current') return 14;
    const selectionGroups = folder ? TOOL_GROUPS : [];
    const groups =
      geo === 'mode' ? (editMode ? [GEOMETRY_GROUP] : selectionGroups)
        : geo === 'groupe' ? [...selectionGroups, GEOMETRY_GROUP]
          : selectionGroups;
    const extra = geo === 'groupe' ? 0 : 1;
    if (groups.length === 0) return 4 + extra;
    if (variant === 'groupes') return 4 + extra + groups.reduce((n, g) => n + g.tools.length, 0);
    if (variant === 'menus') return 4 + extra + groups.length;
    const current = groups.find((g) => g.id === family) ?? groups[0];
    return 4 + extra + 1 + current.tools.length;
  }, [variant, geo, folder, family, editMode]);

  return (
    <UMAppShell
      title={meta.title}
      railItems={railItems}
      activeRailId={panel}
      onRailSelect={(id) => { setPanel((c) => (c === id || ['admin', 'contact', 'logout'].includes(id) ? null : id)); }}
      toolbar={
        <MapToolbar
          variant={variant}
          geo={geo}
          hasBuilding={hasBuilding}
          editMode={editMode}
          onEditModeChange={setEditMode}
          search={search}
          onSearchChange={setSearch}
          selections={FOLDERS}
          selectionLabel={folder}
          onSelectionChange={setFolder}
          onCreateFolder={() => flash('Création d’une sélection (mock)')}
          onCenterSelection={() => mapRef.current?.flyTo(LYON_BELLECOUR, 16)}
          onSelectionActions={() => flash('Actions de la sélection (mock)')}
          activeTool={tool}
          onToolChange={(id) => { setTool(id); if (id) flash(`Outil actif : ${toolLabel(id)}`); }}
          family={family}
          onFamilyChange={setFamily}
          onLocate={() => mapRef.current?.flyTo(LYON_BELLECOUR, 16)}
        />
      }
      leftPanel={
        panel === 'layers' ? (
          <UMLayersPanel onClose={() => setPanel(null)} checked={checked} onCheckedChange={(id, value) => setChecked((p) => ({ ...p, [id]: value }))} />
        ) : undefined
      }
    >
      <UMMap
        ref={mapRef}
        basemap={basemap}
        is3d={is3d}
        sources={{ pipes: DEMO_PIPES, buildings: DEMO_BUILDINGS }}
        layers={layers}
        initialBounds={BELLECOUR_BOUNDS}
        interactiveLayerIds={['urbanmap-building-fill']}
        onModeChange={setMode}
      />
      <UMMapControls
        basemap={basemap}
        availableBasemaps={mode === 'mapbox' ? ['light', 'streets', 'satellite', 'outdoors'] : ['light']}
        onBasemapChange={setBasemap}
        onZoomIn={() => mapRef.current?.zoomIn()}
        onZoomOut={() => mapRef.current?.zoomOut()}
        is3d={is3d}
        onToggle3d={setIs3d}
      />
      {variant !== 'current' && (
        <MeasureControls activeTool={tool} onToolChange={(id) => { setTool(id); if (id) flash(`Outil actif : ${toolLabel(id)}`); }} />
      )}
      {variant !== 'current' && geo === 'contextuelle' && hasBuilding && (
        <GeometryEditBar
          tools={GEOMETRY_GROUP.tools.slice(1)}
          activeTool={tool}
          onToolChange={(id) => { setTool(id); if (id) flash(`Outil actif : ${toolLabel(id)}`); }}
        />
      )}

      <div
        style={{
          position: 'absolute', bottom: 40, right: 16, zIndex: 30, width: 300, background: UMR.white,
          border: `1px solid ${UMR.border}`, boxShadow: 'rgba(0,0,0,0.1) 0 2px 8px', fontFamily: UMR.font,
        }}
      >
        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${UMR.border}`, fontSize: 12, fontWeight: 700, color: UMR.textLabel, letterSpacing: 0.4 }}>
          STUDIO — COMPARER LES VARIANTES
        </div>
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: UMR.textLabel, letterSpacing: 0.4 }}>OUTILS DE SÉLECTION</p>
          {VARIANTS.map((v) => (
            <button
              key={v.id}
              type="button"
              aria-pressed={v.id === variant}
              onClick={() => { setVariant(v.id); setTool(null); }}
              style={{
                textAlign: 'left', padding: '6px 8px', cursor: 'pointer', fontFamily: UMR.font, fontSize: 14,
                border: `1px solid ${v.id === variant ? UMR.blue : 'transparent'}`,
                background: v.id === variant ? UMR.bluePressed : 'transparent', color: UMR.black,
              }}
            >
              {v.label}
            </button>
          ))}
          <p style={{ margin: '4px 0 0', fontSize: 12, lineHeight: '16px', color: UMR.textSecondary }}>{activeVariant.note}</p>

          <p style={{ margin: '8px 0 0', fontSize: 11, fontWeight: 700, color: UMR.textLabel, letterSpacing: 0.4 }}>OUTILS DE GÉOMÉTRIE</p>
          {GEO_DISPLAYS.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-pressed={g.id === geo}
              disabled={variant === 'current'}
              onClick={() => { setGeo(g.id); setTool(null); setEditMode(false); }}
              style={{
                textAlign: 'left', padding: '6px 8px', fontFamily: UMR.font, fontSize: 14,
                cursor: variant === 'current' ? 'not-allowed' : 'pointer',
                border: `1px solid ${g.id === geo && variant !== 'current' ? UMR.blue : 'transparent'}`,
                background: g.id === geo && variant !== 'current' ? UMR.bluePressed : 'transparent',
                color: variant === 'current' ? UMR.textDisabled : UMR.black,
              }}
            >
              {g.label}
            </button>
          ))}
          <p style={{ margin: '4px 0 0', fontSize: 12, lineHeight: '16px', color: UMR.textSecondary }}>
            {variant === 'current' ? 'En « Actuel », les géométries restent dans le panneau du rail.' : activeGeo.note}
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: UMR.textLabel, cursor: 'pointer', marginTop: 8 }}>
            <input
              type="checkbox"
              checked={!!folder}
              onChange={(e) => { setFolder(e.target.checked ? FOLDERS[1] : ''); setTool(null); }}
            />
            Sélection active
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: UMR.textLabel, cursor: 'pointer' }}>
            <input type="checkbox" checked={hasBuilding} onChange={(e) => setHasBuilding(e.target.checked)} />
            Bâtiment dessiné ou sélectionné
          </label>
          <p style={{ margin: 0, fontSize: 12, lineHeight: '16px', color: UMR.textLabel }}>
            Contrôles alignés dans la barre : <strong>{barItems}</strong>
          </p>
        </div>
      </div>

      {notice && (
        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 30 }}>
          <UMMessage>{notice}</UMMessage>
        </div>
      )}
    </UMAppShell>
  );
}
